import { useEffect, useState } from 'react'
import { Button, DialogActions, DialogContent, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import { useHost } from '../../providers/app-provider'
import Api from '../../utils/api'
import useSharedStyles from '../../utils/shared.styles'
import DialogWithTitle from '../dialog-with-title'

import FolderSelectionSearch from './folder-selection-search'
import useStyles from './index.styles'

interface IProps {
  open: boolean
  onClose: () => void
  devices: IDeviceAPIObject[]
  refresh: () => void
}

const MoveDeviceModal = (props: IProps) => {
  const { open, onClose, devices, refresh } = props

  const classes = useStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const host = useHost()
  const currentFolderName = devices[0]?.folderName

  const [selectedFolder, setSelectedFolder] = useState<ISearchResultAccount>()
  const [showSearch, setShowSearch] = useState<boolean>(open)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (open) setShowSearch(true)
  }, [open])

  const closeModal = () => {
    // Reset state and call onClose
    setSelectedFolder(undefined)
    setIsSubmitting(false)
    setOpenModal(false)
    onClose()
  }

  const handleSubmit = async () => {
    if (!selectedFolder) return
    try {
      setIsSubmitting(true)
      await Promise.all(devices.map((device) => Api.put(`${host}/api/nodes/${device.id}`, { folderId: selectedFolder.id })))
        .then((results) => {
          const allSuccessful = results.every((res) => res.ok)
          if (!allSuccessful) {
            enqueueSnackbar(devices.length > 1 ? 'Unable to move all devices. Please try again.' : 'Unable to move device. Please try again.', {
              variant: 'error',
            })
          } else {
            closeModal()
            refresh()
            enqueueSnackbar(devices.length > 1 ? 'Devices moved successfully!' : 'Device moved successfully!', { variant: 'success' })
          }
        })
        .catch(() => {
          enqueueSnackbar(devices.length > 1 ? 'Unable to move all devices. Please try again.' : 'Unable to move device. Please try again.', {
            variant: 'error',
          })
        })
    } catch {
      enqueueSnackbar(devices.length > 1 ? 'Unable to move all devices. Please try again.' : 'Unable to move device. Please try again.', {
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const deviceDisplay = (device: IDeviceAPIObject) =>
    [device.vanity, device.area || device.metadata?.device_location, device.metadata?.device_location_note].filter(Boolean).join(' - ')

  return (
    <>
      <DialogWithTitle setOpen={closeModal} open={openModal} title="Review and Confirm Move" maxWidth="sm" fullWidth={true}>
        <DialogContent>
          <Typography variant="subtitle2">You are moving the following {devices.length > 1 ? 'devices' : 'device'}:</Typography>
          {devices.map((device) => (
            <Typography key={device.id} variant="body1">
              {deviceDisplay(device)}
            </Typography>
          ))}

          <div style={classes.moveFolderCurrentLocationContainer(theme)}>
            <Typography variant="subtitle2" sx={sharedClasses.italicText}>
              Current Location:
            </Typography>
            <Typography variant="body1">{currentFolderName}</Typography>
          </div>

          <div style={classes.moveFolderNewLocationContainer(theme)}>
            <Typography variant="subtitle2" color="primary" sx={sharedClasses.italicText}>
              New Location:
            </Typography>
            <Typography variant="body1">{selectedFolder?.name}</Typography>
          </div>
        </DialogContent>

        <DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeModal}>
              Cancel
            </Button>

            <Button variant="contained" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
              {devices.length > 1 ? 'Move Devices' : 'Move Device'}
            </Button>
          </DialogActions>
        </DialogContent>
      </DialogWithTitle>
      <FolderSelectionSearch
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        onClose={onClose}
        setOpenModal={setOpenModal}
        setSelectedFolder={setSelectedFolder}
      />
    </>
  )
}

export default MoveDeviceModal
